import { PDP_DISPLAY_PRICE_SIMPLE_TYPE } from '@/components/blocks/HDMPriceDisplay/constants';
import { FormatPrice } from '@/components/blocks/HDMPriceDisplay/FormatPrice';
import { styles, unitStyles } from '@/components/blocks/HDMPriceDisplay/style/priceCard';
import { PriceVariant } from '@/components/blocks/HDMPriceDisplay/types';
import { ProductDisplayPrice } from '@/data/types/Product';
import { Box, SxProps, Typography } from '@mui/material';
import { memo } from 'react';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

type HDMPriceDisplayProps = {
	price: ProductDisplayPrice;
	type?: string;
	showRange?: boolean;
	variant?: PriceVariant;
	sx?: SxProps;
};

export const HDMPriceDisplay: React.FC<HDMPriceDisplayProps> = ({
	price,
	type,
	showRange = false,
	variant = 'md',
	sx,
}) => {
	const { offer, min, max } = price;

	if (showRange && typeof min === 'number' && typeof max === 'number') {
		return <RangePrice variant={variant} price={price} sx={sx} />;
	} else if (typeof offer === 'number') {
		return <OfferPrice variant={variant} price={price} type={type} sx={sx} />;
	}

	return null;
};

const RangePrice = ({
	price: { min, max },
	variant,
	sx,
}: {
	variant?: PriceVariant;
	price: ProductDisplayPrice;
	sx?: SxProps;
}) => {
	if (typeof min !== 'number' || typeof max !== 'number') return null;

	return (
		<Box sx={sx}>
			<Box sx={styles.bundlePriceContainer}>
				<Box sx={styles.mainPriceAndUnit}>
					{min !== max ? (
						<>
							<FormatPrice price={min} variant={variant} />
							<Box component="span" sx={styles.dashSeparator}>
								{'-'}
							</Box>
							<FormatPrice price={max} variant={variant} />
						</>
					) : (
						<FormatPrice price={min} variant={variant} />
					)}
				</Box>
			</Box>
		</Box>
	);
};

const OfferPrice = ({
	price: { offer, list, singleUnit, wholeUnit, wholeOffer, wholeList },
	type,
	variant,
	sx,
}: {
	price: ProductDisplayPrice;
	variant: PriceVariant;
	type?: string;
	savings?: number;
	sx?: SxProps;
}) => {
	const isSimple = !!type && type === PDP_DISPLAY_PRICE_SIMPLE_TYPE;

	return (
		<Box sx={sx}>
			<Box sx={styles.mainPriceContainer}>
				<Offer variant={variant} offer={offer} singleUnit={singleUnit} />
				<List variant={variant} list={list} offer={offer} isSimple={isSimple} />
			</Box>

			<WholeOffer
				wholeOffer={wholeOffer}
				wholeList={wholeList}
				wholeUnit={wholeUnit}
				isSimple={isSimple}
			/>
		</Box>
	);
};

const Offer = ({
	variant,
	offer,
	singleUnit,
}: {
	variant: PriceVariant;
	offer: number | undefined | null;
	singleUnit: string | undefined | null;
}) => {
	if (typeof offer !== 'number') return null;

	return (
		<Box sx={styles.mainPriceAndUnit} id="offerPrice">
			<FormatPrice price={offer} variant={variant} />

			<OfferSingleUnit variant='xs' singleUnit={singleUnit} />
		</Box>
	);
};

const OfferSingleUnit = memo(
	({ variant, singleUnit }: { variant: PriceVariant; singleUnit: string | undefined | null }) => {
		const renderSingleUnit = (singleUnit: string) => {
			const regExpressionSingleUnit = singleUnit && singleUnit.match(/^([^\d]+)(\d+)$/);
			if (regExpressionSingleUnit) {
				const singleUnitLetter = regExpressionSingleUnit[1];
				const singleUnitNumber = regExpressionSingleUnit[2];

				return (
					<>
						{singleUnitLetter}
						<Typography component="sup" sx={styles.singleUnit}>
							{singleUnitNumber}
						</Typography>
					</>
				);
			}

			return singleUnit;
		};

		if (!singleUnit) return null;

		return (
			<Typography component="span" sx={unitStyles(variant)}>
				{'/'} {renderSingleUnit(singleUnit)}
			</Typography>
		);
	}
);

const List = ({
	variant,
	list,
	offer,
	isSimple,
}: {
	variant: PriceVariant;
	list: number | undefined | null;
	offer: number | undefined | null;
	isSimple: boolean;
}) => {
	if (typeof list !== 'number' || typeof offer !== 'number' || list <= offer) return null;

	const discountPercentage = list !== 0 ? (list - offer) / list : 0;
	const formattedDiscount = Math.trunc(discountPercentage * 100);

	return (
		<Box sx={styles.discountAndListPriceContainer}>
			{formattedDiscount > 0 ? (
				<Box sx={styles.discountBadge}>{`-${formattedDiscount}%`}</Box>
			) : null}

			{!isSimple ? (
				<Typography variant={'bodySubText'} sx={styles.listPrice}>
					<FormatPrice price={list} type={PDP_DISPLAY_PRICE_SIMPLE_TYPE} variant={variant} />
				</Typography>
			) : null}
		</Box>
	);
};

const WholeOffer = ({
	wholeOffer,
	wholeList,
	wholeUnit,
	isSimple,
}: {
	wholeOffer: number | undefined | null;
	wholeList: number | undefined | null;
	wholeUnit: string | undefined | null;
	isSimple: boolean;
}) => {
	if (isSimple || typeof wholeOffer !== 'number' || wholeOffer <= 0) return null;

	return (
		<Box sx={styles.wholeUnitContainer} id="productPricePerUnit">
			<Box sx={styles.wholeOfferContainer}>
				<Typography variant="bodyBaseline" sx={styles.wholeOfferText}>
					{wholeUnit ? wholeUnit : null}
				</Typography>
				<Typography
					variant="bodyBaseline"
					fontWeight={OpenSansFontWeights.semibold}
					sx={styles.wholeOffer}
				>
					{`$${wholeOffer.toFixed(2)}`}
				</Typography>
			</Box>
			{wholeList && wholeOffer && wholeList > wholeOffer ? (
				<>
					<Box sx={styles.wholeSeparator} />
					<Typography variant="bodyBaseline" sx={styles.wholeList}>
						{`$${wholeList.toFixed(2)}`}
					</Typography>
				</>
			) : null}
		</Box>
	);
};
